/* Read SENSEA-THEME.md before editing. Rules are enforced by CI. */
/* sensea chapter swipe (blocks/sensea-chapters.liquid). Below 990px the sections after the block's section
   become one swipe row with chips, each chapter a little narrower than the screen so the next one peeks in;
   the row takes the height of the chapter in view. From 990px the CSS stacks
   them again, and with the block's "chapter list" setting the chips become a sticky numbered list on the left
   that marks the chapter being read. Not run in the theme editor, so sections stay editable there. */

const DESKTOP = window.matchMedia('(min-width: 990px)');
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

class SenseaChapters extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready || window.Shopify?.designMode) return;
    const host = this.closest('.shopify-section');
    const nav = this.querySelector('.sensea-chapters__nav');
    if (!host || !nav) return;

    this.chips = [...nav.querySelectorAll('.sensea-chapters__chip')];
    this.slides = [];
    for (let el = host.nextElementSibling; el && this.slides.length < this.chips.length; el = el.nextElementSibling) {
      if (el.classList.contains('shopify-section')) this.slides.push(el);
    }
    if (this.slides.length < 2) return;
    this.chips.length = this.slides.length;
    this.dataset.ready = 'true';

    const wrap = document.createElement('div');
    const scheme = [...nav.classList].find((c) => c.startsWith('sensea-scheme-'));
    this.hasIndex = this.dataset.index === 'true';
    wrap.className = `sensea-chapters__wrap ${this.hasIndex ? 'sensea-chapters__wrap--index' : ''} ${scheme || ''}`;
    this.track = document.createElement('div');
    this.track.className = 'sensea-chapters__track';
    host.after(wrap);
    wrap.append(nav, this.track);
    this.track.append(...this.slides);
    this.wrap = wrap;
    this.chipRow = nav.querySelector('.sensea-chapters__chips');

    this.slides.forEach((slide, i) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-label', this.chips[i].textContent.trim());
      this.chips[i].addEventListener('click', () => this.go(i, true));
      if (i < this.slides.length - 1) {
        const row = document.createElement('div');
        row.className = `sensea-chapters__next-row ${scheme || ''}`;
        const next = document.createElement('button');
        next.type = 'button';
        next.className = 'sensea-chapters__next';
        next.textContent = `${this.dataset.next || ''} ${this.chips[i + 1].textContent.trim()} →`.trim();
        next.addEventListener('click', () => this.go(i + 1, true));
        row.append(next);
        slide.append(row);
      }
    });

    this.index = -1;
    let t;
    this.track.addEventListener('scroll', () => {
      clearTimeout(t);
      t = setTimeout(() => this.sync(), 80);
    }, { passive: true });
    new ResizeObserver(() => {
      if (this.index >= 0 && !DESKTOP.matches) this.track.scrollLeft = this.left(this.index);
      this.fit();
    }).observe(this.track);
    this.slides.forEach((s) => new ResizeObserver(() => this.fit()).observe(s));
    DESKTOP.addEventListener('change', () => this.fit());
    window.addEventListener('hashchange', () => this.toHash());
    if (this.hasIndex) {
      let queued = false;
      // Capture phase: the page may scroll inside an element rather than the window.
      document.addEventListener('scroll', () => {
        if (queued || !DESKTOP.matches) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          this.readPosition();
        });
      }, { passive: true, capture: true });
    }

    this.sync();
    this.toHash();
  }

  /* Show chapter i; with toTop, bring the start of the chapter into view below the chips. */
  go(i, toTop) {
    if (DESKTOP.matches) {
      this.slides[i].scrollIntoView({ behavior: REDUCED.matches ? 'auto' : 'smooth' });
      return;
    }
    const behavior = REDUCED.matches ? 'auto' : 'smooth';
    this.track.scrollTo({ left: this.left(i), behavior });
    this.mark(i);
    if (toTop) {
      const offset = this.wrap.getBoundingClientRect().top;
      if (offset < 0) window.scrollBy({ top: offset, behavior });
    }
  }

  /* Chapters are narrower than the row (the next one peeks in), so positions come from the slides, not the row width. */
  left(i) {
    return this.slides[i].offsetLeft - this.slides[0].offsetLeft;
  }

  stride() {
    return Math.max(this.left(1), 1);
  }

  /* Desktop list: the chapter whose top has passed the upper third of the screen is the one being read. */
  readPosition() {
    const line = window.innerHeight / 3;
    let i = 0;
    this.slides.forEach((s, k) => {
      if (s.getBoundingClientRect().top <= line) i = k;
    });
    this.mark(i, true);
  }

  sync() {
    if (DESKTOP.matches) {
      if (this.hasIndex) this.readPosition();
      return this.fit();
    }
    this.mark(Math.round(this.track.scrollLeft / this.stride()));
  }

  mark(i, instant) {
    i = Math.max(0, Math.min(i, this.slides.length - 1));
    if (i === this.index) return this.fit();
    this.index = i;
    this.chips.forEach((c, k) => c.setAttribute('aria-current', k === i ? 'true' : 'false'));
    const chip = this.chips[i].parentElement;
    if (!DESKTOP.matches) this.chipRow.scrollTo({ left: chip.offsetLeft - 20, behavior: instant || REDUCED.matches ? 'auto' : 'smooth' });
    this.fit();
  }

  fit() {
    if (DESKTOP.matches || this.index < 0) {
      this.track.style.removeProperty('--sensea-chapter-height');
      return;
    }
    this.track.style.setProperty('--sensea-chapter-height', `${this.slides[this.index].offsetHeight}px`);
  }

  toHash() {
    const id = decodeURIComponent(location.hash.slice(1));
    const target = id && document.getElementById(id);
    const i = target ? this.slides.findIndex((s) => s.contains(target)) : -1;
    if (i < 0 || DESKTOP.matches) return;
    this.track.scrollTo({ left: this.left(i) });
    this.mark(i, true);
    requestAnimationFrame(() => target.scrollIntoView());
  }
}

if (!customElements.get('sensea-chapters')) customElements.define('sensea-chapters', SenseaChapters);
